import { ChangeDetectorRef, ViewRef } from '@angular/core';
import { MonoTypeOperatorFunction } from 'rxjs';
import { finalize } from 'rxjs/operators';

/**
 * One request-lifecycle primitive for every loader in the console.
 *
 * `.pipe(endLoading(this.changeDetectorRef, () => { this.isLoading = false; }))`
 *
 * It answers the two failure modes that have stranded loaders in this app:
 *
 * 1. **The flag never gets cleared.** Reset code written at the end of a `next`
 *    handler is skipped whenever anything above it throws — a null field reaching a
 *    validator, a payload shape the mapping did not expect. RxJS routes such a throw
 *    to the *unhandled* error path, so the console stays clean and it looks exactly
 *    like a response that never arrived: a spinner over a request that answered 200.
 *    `finalize` runs on success, on error, on an exception thrown inside the
 *    subscriber, and on unsubscribe — there is no path out that skips it.
 *
 * 2. **The flag is cleared but the view does not repaint** until the user happens to
 *    click something. Angular 22 compiles every component as OnPush unless it opts
 *    out with `ChangeDetectionStrategy.Eager`, so assigning to a plain field inside a
 *    `subscribe()` marks nothing dirty: the zone's tick after the XHR walks straight
 *    past the component. `detectChanges()` here repaints that view synchronously as
 *    the request settles — which is why no click, `setTimeout` or `NgZone.run` dance
 *    is needed anywhere.
 *
 * The `ViewRef.destroyed` check is what makes it safe to use without a `takeUntil`:
 * these screens deliberately let a request outlive the view (switching tabs
 * mid-request is normal), and `detectChanges()` on a destroyed view throws.
 *
 * @param cdRef the component's own ChangeDetectorRef
 * @param done  clears the loading state — flags only, no rendering work
 */
export function endLoading<T>(cdRef: ChangeDetectorRef, done: () => void): MonoTypeOperatorFunction<T> {
  return finalize(() => {
    done();
    if (!(cdRef as ViewRef).destroyed) {
      cdRef.detectChanges();
    }
  });
}

/**
 * Repaint now, unless the view is gone. For the non-HTTP half of the same problem:
 * optimistic updates, modal results and promise callbacks that change what is on
 * screen from outside a request pipeline.
 */
export function repaintView(cdRef: ChangeDetectorRef): void {
  if (!(cdRef as ViewRef).destroyed) {
    cdRef.detectChanges();
  }
}
